/**
 * PageHeaderContext — lets individual pages take over the mobile
 * header (title + right-side action icons) without re-implementing
 * the whole header per page.
 *
 * Usage in a page:
 *   import { usePageHeader } from '../context/PageHeaderContext';
 *   ...
 *   usePageHeader({
 *     title: 'Calls',
 *     back: null,                  // or a path string for a back arrow
 *     rightSlot: (                 // JSX placed before the bell icon
 *       <>
 *         <button onClick={...} aria-label="History">
 *           <Clock className="w-5 h-5" />
 *         </button>
 *       </>
 *     ),
 *   }, [/* deps that should re-run the effect *\/]);
 *
 * When a page mounts and calls this, Layout's mobile + desktop
 * headers both swap in the override. When the page unmounts, the
 * header reverts to the default (logo + brand label).
 *
 * Desktop header mirrors the same swap pattern: the logo/brand area
 * becomes back+title, and rightSlot lands immediately before the
 * notification bell. The hamburger menu stays put so users still
 * have a global nav entrypoint.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';

const PageHeaderContext = createContext({ config: null, setConfig: () => {} });

export const PageHeaderProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  return (
    <PageHeaderContext.Provider value={{ config, setConfig }}>
      {children}
    </PageHeaderContext.Provider>
  );
};

export const usePageHeaderState = () => useContext(PageHeaderContext);

/**
 * Hook used by pages. The deps array works exactly like useEffect's —
 * pass values that, when changed, should refresh the header (e.g. a
 * dynamic title, a feature flag).
 */
export const usePageHeader = (config, deps = []) => {
  const { setConfig } = useContext(PageHeaderContext);
  useEffect(() => {
    setConfig(config);
    return () => setConfig(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

export default PageHeaderContext;
